import { AppIcon } from '@/components/ui/app-icon';
import { createFileRoute } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { iconCircleCheck, iconStar, iconStarSolid } from '@/lib/icons';
import { PRIMARY_BUTTON } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { BRAND, brandAsset, siteUrl } from '@/lib/brand';

const REASONS = [
  "I don't use LinkedIn for prospecting anymore",
  'It slowed down my browser',
  'I found a better tool',
  "It wasn't finding accurate data",
  'Too expensive',
  'I had technical issues',
  "I didn't understand how to use it",
  'Other',
];

const RATING_LABELS = ['Terrible', 'Poor', 'Okay', 'Good', 'Excellent'];

const StarRating = ({
  rating,
  onRate,
}: {
  rating: number;
  onRate: (r: number) => void;
}) => {
  const [hovered, setHovered] = useState(0);
  const active = hovered || rating;

  return (
    <div className='space-y-2 text-center'>
      <p className='text-sm font-medium text-foreground'>
        How would you rate your overall experience?
      </p>
      <div className='flex items-center justify-center gap-1'>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type='button'
            aria-label={`${star} ${star === 1 ? 'star' : 'stars'}: ${RATING_LABELS[star - 1]}`}
            aria-pressed={rating === star}
            onClick={() => onRate(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            className='rounded-md p-1 transition-transform hover:scale-110 focus-visible:outline-2 focus-visible:outline-ring'
          >
            {star <= active ? (
              <AppIcon
                icon={iconStarSolid}
                className='text-yellow-400 transition-colors'
                style={{ fontSize: 32 }}
              />
            ) : (
              <AppIcon
                icon={iconStar}
                mono
                className='text-muted-foreground/40 transition-colors'
                style={{ fontSize: 32 }}
              />
            )}
          </button>
        ))}
        {active > 0 && (
          <span className='ml-2 text-sm text-muted-foreground'>
            {RATING_LABELS[active - 1]}
          </span>
        )}
      </div>
    </div>
  );
};

const ExtensionFeedback = () => {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [rating, setRating] = useState(0);
  const [additionalFeedback, setAdditionalFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const pending = useRef(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!selectedReason || pending.current) return;
    pending.current = true;
    setSubmitting(true);
    setError('');

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const response = await fetch(
        `${apiUrl}/api/v1/feedback/extension-uninstall`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            reason: selectedReason,
            rating: rating || undefined,
            feedback: additionalFeedback || undefined,
          }),
        }
      );
      if (!response.ok) throw new Error('Feedback request failed');
      setSubmitted(true);
    } catch {
      setError(
        'Could not send your feedback. Your answers are still here. Please try again.'
      );
    } finally {
      pending.current = false;
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-background'>
        <div className='mx-4 w-full max-w-md space-y-6 text-center'>
          <div className='mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10'>
            <AppIcon
              icon={iconCircleCheck}
              className='text-primary'
              style={{ fontSize: 32 }}
            />
          </div>
          <h1 className='text-2xl font-bold text-foreground'>
            Thank you for your feedback
          </h1>
          <p className='text-muted-foreground'>
            We appreciate you taking the time to help us improve {BRAND.name}.
            Your feedback makes a difference.
          </p>
          <a
            href={siteUrl('https://cleanlist.ai')}
            className={cn(
              PRIMARY_BUTTON,
              'inline-block px-6 py-2.5 text-sm font-medium'
            )}
          >
            Visit {BRAND.dotName}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className='flex min-h-screen items-center justify-center bg-background'>
      <div className='mx-4 w-full max-w-md space-y-6'>
        <div className='text-center'>
          <img
            src={brandAsset('/logo-dark.png')}
            alt={BRAND.name}
            className='mx-auto mb-4 hidden h-8 dark:block'
          />
          <img
            src={brandAsset('/logo-light.png')}
            alt={BRAND.name}
            className='mx-auto mb-4 block h-8 dark:hidden'
          />
          <h1 className='text-2xl font-bold text-foreground'>
            We're sorry to see you go
          </h1>
          <p className='mt-2 text-muted-foreground'>
            Help us improve by sharing why you uninstalled the {BRAND.name}{' '}
            extension.
          </p>
        </div>

        <fieldset disabled={submitting} className='space-y-6'>
          <StarRating rating={rating} onRate={setRating} />

          <div className='space-y-2'>
            {REASONS.map((reason) => (
              <button
                key={reason}
                type='button'
                aria-pressed={selectedReason === reason}
                onClick={() => setSelectedReason(reason)}
                className={`w-full rounded-lg border px-4 py-3 text-left text-sm transition-all ${
                  selectedReason === reason
                    ? 'border-primary bg-primary/5 text-foreground'
                    : 'border-border bg-card text-foreground hover:border-primary/50'
                }`}
              >
                {reason}
              </button>
            ))}
          </div>

          <textarea
            aria-label='Additional feedback'
            placeholder='More feedback? (optional)'
            value={additionalFeedback}
            onChange={(e) => setAdditionalFeedback(e.target.value)}
            className='w-full rounded-lg border border-border bg-card px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary focus:outline-hidden'
            rows={3}
          />

          {error && (
            <p role='alert' className='text-sm text-destructive'>
              {error}
            </p>
          )}
          <button
            type='button'
            onClick={handleSubmit}
            disabled={!selectedReason || submitting}
            className={cn(
              PRIMARY_BUTTON,
              'w-full px-6 py-2.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            {submitting
              ? 'Sending feedback…'
              : error
                ? 'Retry feedback'
                : 'Send feedback'}
          </button>
        </fieldset>

        <p className='text-center text-xs text-muted-foreground'>
          Changed your mind?{' '}
          <a
            href='https://chromewebstore.google.com/detail/cleanlistai/njbepggbhklfpljfcmpnonjdolibjadj'
            className='text-primary hover:underline'
          >
            Reinstall the extension
          </a>
        </p>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/extension-feedback')({
  component: ExtensionFeedback,
});
